/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  assertUnreachable,
  Duration,
  HttpStatusCode,
  LoginTokenScope,
  TalerMerchantApi,
} from "@gnu-taler/taler-util";
import {
  ButtonBetterBulma,
  LocalNotificationBannerBulma,
  useChallengeHandler,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, h, VNode } from "preact";
import { useState } from "preact/hooks";
import {
  FormErrors,
  FormProvider,
  TalerForm,
} from "../../../../components/form/FormProvider.js";
import { Input } from "../../../../components/form/Input.js";
import { SolveMFAChallenges } from "../../../../components/SolveMFA.js";
import { Tooltip } from "../../../../components/Tooltip.js";
import { useSessionContext } from "../../../../context/session.js";
import { usePreference } from "../../../../hooks/preference.js";
import { undefinedIfEmpty } from "../../../../utils/table.js";
import { maybeTryFirstMFA } from "../../accounts/create/CreatePage.js";

const TALER_SCREEN_ID = 29;

type Entity = {
  name: string;
  password: string;
} & TalerForm;

interface Props {
  onCreated: (asd: TalerMerchantApi.LoginTokenSuccessResponse) => void;
  onBack?: () => void;
}


export function CreatePage({ onCreated, onBack }: Props): VNode {
  const { i18n } = useTranslationContext();

  const [state, setState] = useState<Partial<Entity>>({});
  const { state: session, lib } = useSessionContext();

  const errors = undefinedIfEmpty<FormErrors<Entity>>({
    password: !state.password ? i18n.str`Required` : undefined,
    name: !state.name ? i18n.str`Required` : undefined,
  });

  const hasErrors = errors !== undefined;

  const [notification, safeFunctionHandler] = useLocalNotificationBetter();
  const mfa = useChallengeHandler();

  const data: TalerMerchantApi.LoginTokenRequest = {
    scope: LoginTokenScope.OrderPos_Refreshable,
    description: `POS access from: ${state.name}`,
    duration: Duration.toTalerProtocolDuration(Duration.fromSpec({days:10})),
  };

  const create = safeFunctionHandler(
    i18n.str`create pos access token`,
    async (
      pwd: string,
      request: TalerMerchantApi.LoginTokenRequest,
      challengeIds: string[],
    ) => {
      const resp = await lib.instance.createAccessToken(session.instance, pwd, request, {
        challengeIds,
      })
      if (resp.type === "fail" && resp.case === HttpStatusCode.Accepted) {
        await maybeTryFirstMFA(lib.instance, mfa, resp.body)
      }
      return resp
    },
    !!errors || !state.password ? undefined : [state.password, data, []],
  );
  create.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Accepted:
        return undefined;
      case HttpStatusCode.Unauthorized:
        return i18n.str`Check the password.`;
      case HttpStatusCode.NotFound:
        return i18n.str`Instance not found.`;
      default:
        assertUnreachable(fail);
    }
  };
  create.onSuccess = onCreated;

  const retry = create.lambda((ids: string[]) => [
    create.args![0],
    create.args![1],
    ids,
  ]);

  if (mfa.pendingChallenge) {
    return (
      <SolveMFAChallenges
        currentChallenge={mfa.pendingChallenge}
        initial={mfa.initial}
        focus
        onCompleted={retry}
        onCancel={mfa.doCancelChallenge}
      />
    );
  }
  return (
    <Fragment>
      <LocalNotificationBannerBulma notification={notification} />
      <section class="section is-main-section">
        <div class="columns">
          <div class="column" />
          <div class="column is-four-fifths">
            <FormProvider
              object={state}
              valueHandler={setState}
              errors={errors}
            >
              <Input<Entity>
                name="name"
                label={i18n.str`Device name`}
                help={i18n.str`Helps you remember where this access token is being used before deleting it.`}
              />

              <Input<Entity>
                name="password"
                inputType="password"
                label={i18n.str`Current password`}
              />
              <div class="buttons is-right mt-5">
                {onBack && (
                  <button class="button" type="button" onClick={onBack}>
                    <i18n.Translate>Cancel</i18n.Translate>
                  </button>
                )}
                <Tooltip
                  text={
                    hasErrors
                      ? i18n.str`Please complete the marked fields`
                      : i18n.str`Confirm operation`
                  }
                >
                  <ButtonBetterBulma type="submit" onClick={create}>
                    <i18n.Translate>Confirm</i18n.Translate>
                  </ButtonBetterBulma>
                </Tooltip>
              </div>
            </FormProvider>
          </div>
          <div class="column" />
        </div>
      </section>
    </Fragment>
  );
}
